import { Box, Flex } from '@chakra-ui/react';
import type { SelectOption } from '@diamond/shared/types';
import { Button, DateFilter, Searchbox, Select } from '@diamond/shared/ui';
import type { KeyboardEvent } from 'react';
import type {
  FieldValues,
  UseFormRegister,
  UseFormReturn,
} from 'react-hook-form';
import { FormProvider } from 'react-hook-form';

import {
  VOUCHER_MECHANISM_OPTIONS,
  VOUCHER_STATUS_OPTIONS,
} from '../constants';

// Type definitions
type DateFilterFields = {
  'voucher_period-from': string;
  'voucher_period-to': string;
};

interface VoucherFilterBarProps {
  methods: UseFormReturn<DateFilterFields>;
  searchRegister: UseFormRegister<FieldValues>;
  handleSearch: () => void;
  handleDateFilter: (period: string) => { fromDate: string; toDate: string };
  updateDateParams: (fromDate: string, toDate: string) => void;
  rewardTypeValue: SelectOption | null;
  onRewardTypeChange: (newValue: SelectOption | null) => void;
  statusVoucherValue: SelectOption | null;
  onStatusVoucherChange: (newValue: SelectOption | null) => void;
}

// Constants
const FILTER_SELECT_WIDTH = { base: 'full', md: '220px' };
const SEARCH_MIN_WIDTH = { base: '100%', md: '320px' };

const DATE_FILTER_CONFIG = {
  fromField: 'voucher_period-from' as const,
  toField: 'voucher_period-to' as const,
  buttonName: 'Periode Promo',
  shortYearFormat: true,
  buttonVariant: 'outline' as const,
};

const SEARCH_CONFIG = {
  placeholder: 'Cari brand atau Kode voucher',
  buttonText: 'Cari',
};

// Helper Components
function FilterSelect({
  id,
  placeholder,
  options,
  value,
  onChange,
}: {
  id: string;
  placeholder: string;
  options: SelectOption[];
  value: SelectOption | null;
  onChange: (newValue: SelectOption | null) => void;
}) {
  return (
    <Box w={FILTER_SELECT_WIDTH}>
      <Select
        id={id}
        placeholder={placeholder}
        options={options}
        value={value}
        onChange={onChange}
      />
    </Box>
  );
}

function SearchSection({
  register,
  onSearch,
}: {
  register: UseFormRegister<FieldValues>;
  onSearch: () => void;
}) {
  const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSearch();
    }
  };

  return (
    <Flex flex="1" minW={SEARCH_MIN_WIDTH} gap={3} alignItems="center">
      <Searchbox
        register={register}
        placeholder={SEARCH_CONFIG.placeholder}
        onKeyPress={handleKeyPress}
        w="full"
      />
      <Button
        variant="outline"
        borderColor="blue.500"
        color="blue.600"
        _hover={{ bg: 'blue.50' }}
        flexShrink={0}
        onClick={onSearch}
      >
        {SEARCH_CONFIG.buttonText}
      </Button>
    </Flex>
  );
}

// Main Component
export function VoucherFilterBar({
  methods,
  searchRegister,
  handleSearch,
  handleDateFilter,
  updateDateParams,
  rewardTypeValue,
  onRewardTypeChange,
  statusVoucherValue,
  onStatusVoucherChange,
}: VoucherFilterBarProps) {
  return (
    <FormProvider {...methods}>
      <Flex
        w="full"
        gap={3}
        alignItems={{ base: 'stretch', md: 'center' }}
        flexWrap="wrap"
      >
        <Flex
          flex="1"
          gap={3}
          alignItems={{ base: 'stretch', md: 'center' }}
          flexWrap="wrap"
        >
          {/* Date Filter */}
          <DateFilter
            fromField={DATE_FILTER_CONFIG.fromField}
            toField={DATE_FILTER_CONFIG.toField}
            shortYearFormat={DATE_FILTER_CONFIG.shortYearFormat}
            buttonVariant={DATE_FILTER_CONFIG.buttonVariant}
            buttonName={DATE_FILTER_CONFIG.buttonName}
            onFilterChange={handleDateFilter}
            onCustomDateChange={updateDateParams}
          />

          {/* Mechanism Filter */}
          <FilterSelect
            id="mechanism"
            placeholder="Mekanisme"
            options={VOUCHER_MECHANISM_OPTIONS}
            value={rewardTypeValue}
            onChange={onRewardTypeChange}
          />

          {/* Status Filter */}
          <FilterSelect
            id="status"
            placeholder="Status Voucher"
            options={VOUCHER_STATUS_OPTIONS}
            value={statusVoucherValue}
            onChange={onStatusVoucherChange}
          />

          {/* Search Box */}
          <SearchSection register={searchRegister} onSearch={handleSearch} />
        </Flex>
      </Flex>
    </FormProvider>
  );
}
